import produce from 'immer';
import without from 'lodash/without';

import {
	getEmojiFromPublishedPageProperties,
	getEmojiFromDraftPageProperties,
} from '@confluence/emoji-title/entry-points/transformer.ts';
import { getTitleAndEmoji } from '@confluence/emoji-title/entry-points/helpers.ts';
import { getEditorVersionFromPageProperties } from '@confluence/content-utils/entry-points/editorVersion.ts';
import type {
	ItemId,
	TreeObjectItems,
	TreeItem,
} from '@confluence/tree/entry-points/tree-types.ts';

import {
	childrenAreLatest,
	getDirectionalCursors,
	getIds,
	getPages,
	getRootId,
	isPageTreeTruncatedPrevious,
	isPageTreeTruncatedFollowing,
	mergePages,
} from './data-extractors.ts';
import { VIRTUAL_ROOT_ID, VIRTUAL_LOCAL_ROOT_ID } from './virtualRootIds.js';

// TODO: various types here can/should be narrowed by making createTreeItem more robust
export interface ContentTreeItem extends TreeItem {
	parentId?: ItemId | null;
	isChildrenLoading: boolean;
	hasRestrictions?: boolean;
	hasInheritedRestrictions?: boolean;
	data: {
		title?: string;
		type?: string;
		fullTitle?: string;
		status?: string;
		blank?: boolean;
		emoji?: string;
		emojiFromProperties?: string;
		webui?: string;
		editorVersion?: string;
		isSelected?: boolean;
		isHighlighted?: boolean;
		subType?: string;
		createdDate?: string;
		isCursor?: boolean;
		isOptimistic?: boolean;
	};
}

export const isManagedFolder = (page: ContentTreeItem['data'] | null | undefined): boolean =>
	page?.type === 'folder' && page?.subType === 'managed';

const DRAFT_BLOCKED_INSTRUCTIONS: NonNullable<TreeItem['blockedInstructions']> = ['make-child'];
const MANAGED_FOLDER_BLOCKED_INSTRUCTIONS: NonNullable<TreeItem['blockedInstructions']> = [
	'make-child',
];
const MANAGED_FOLDER_CHILD_BLOCKED_INSTRUCTIONS: NonNullable<TreeItem['blockedInstructions']> = [
	'reorder-above',
	'reorder-below',
	'make-child',
	'reparent',
];

const getBlockedInstructions = (page: ContentTreeItem['data']): TreeItem['blockedInstructions'] => {
	// Managed folders themselves can be moved, but nothing can be dropped INTO them.
	// reorder-above and reorder-below are still allowed to place items next to the folder.
	if (isManagedFolder(page)) {
		return MANAGED_FOLDER_BLOCKED_INSTRUCTIONS;
	}
	// Drafts cannot have items dropped into them
	if (page.status !== 'CURRENT') {
		return DRAFT_BLOCKED_INSTRUCTIONS;
	}
	return undefined;
};

/**
 * Post-processes a tree items dictionary to set blockedInstructions on
 * children of managed folders. This ensures all items inside managed
 * folders have drop indicators blocked regardless of how they were added
 * to the tree (initial load, expand, optimistic creation, etc.).
 */
export const applyManagedFolderRestrictions = (
	items: TreeObjectItems<ContentTreeItem>,
): TreeObjectItems<ContentTreeItem> => {
	let result: TreeObjectItems<ContentTreeItem> | undefined;

	for (const id of Object.keys(items)) {
		const item = items[id];
		if (!item.parentId) {
			continue;
		}
		const parent = items[item.parentId];
		if (
			parent &&
			isManagedFolder(parent.data) &&
			item.blockedInstructions !== MANAGED_FOLDER_CHILD_BLOCKED_INSTRUCTIONS
		) {
			result = result ?? { ...items };
			result[id] = {
				...item,
				blockedInstructions: MANAGED_FOLDER_CHILD_BLOCKED_INSTRUCTIONS,
			};
		}
	}

	return result ?? items;
};

export const createTreeItem = (page: any, isWhiteboardEmojiFGEnabled = false): ContentTreeItem => {
	const {
		text: title,
		emoji: displayEmoji,
		selectedEmoji: emojiFromProperties,
	} = getTitleAndEmoji(
		page.title,
		getEmojiFromPublishedPageProperties(page),
		getEmojiFromDraftPageProperties(page),
		page.status,
		undefined,
		isWhiteboardEmojiFGEnabled || page.type !== 'whiteboard',
	);
	const editorVersion = getEditorVersionFromPageProperties(page);
	// editor should be v1, v2, or ""

	return {
		id: page.id,
		children: getPages(page.children),
		parentId: page.parentId || null,
		hasChildren: page.hasChildren,
		isExpanded: false,
		isChildrenLoading: false,
		hasRestrictions: page.hasRestrictions || false,
		hasInheritedRestrictions: page.hasInheritedRestrictions || false,
		blockedInstructions: getBlockedInstructions(page),
		data: {
			title: title || '',
			fullTitle: page.title,
			status: page.status,
			blank: page.blank,
			emoji: displayEmoji,
			emojiFromProperties,
			webui: page.links?.webui,
			type: page.type,
			editorVersion,
			subType: page.subType,
			createdDate: page.createdDate?.value,
			isSelected: page.isSelected,
			isOptimistic: page.isOptimistic,
		},
	};
};

/**
 * Flattens the data retrieved relative to a currentPage in order to make the
 * tree renderable top-down.
 *
 * @param {object} currentPage
 * @param {object} dictionary
 */
export const flattenCurrentTree = (
	currentPage: any,
	dictionary = {},
	isWhiteboardEmojiFGEnabled = false,
): TreeObjectItems<ContentTreeItem> | undefined => {
	if (!currentPage) {
		return;
	}

	if (dictionary[currentPage.id]) {
		//there should be no duplicates in server data
		throw new Error('pageTree Data malformed: contains duplicate pages');
	}

	const children = getPages(currentPage.children);
	const ancestors = getPages(currentPage.nearestAncestors);
	const siblings = [
		...getPages(currentPage.previousSiblings),
		...getPages(currentPage.followingSiblings),
	];
	dictionary[currentPage.id] = createTreeItem(currentPage, isWhiteboardEmojiFGEnabled);
	siblings.forEach((sibling) =>
		flattenCurrentTree(sibling, dictionary, isWhiteboardEmojiFGEnabled),
	);
	ancestors.forEach((ancestor) =>
		flattenCurrentTree(ancestor, dictionary, isWhiteboardEmojiFGEnabled),
	);
	children.forEach((child) => flattenCurrentTree(child, dictionary, isWhiteboardEmojiFGEnabled));

	return dictionary;
};

/**
 * Flattens the root level pages (including orphans and space home page), and
 * creates a virtual root that all root level pages are descended from.
 *
 * @param {object} rootLevel
 * @param {object} dictionary
 */
export const flattenRootLevelTree = (rootLevel: any, dictionary = {}, immutable = true) => {
	const rootLevelPages = getPages(rootLevel).map((page) => {
		const rootLevelPage = createTreeItem(page);
		dictionary[page.id] = rootLevelPage;

		return rootLevelPage;
	});

	dictionary[VIRTUAL_ROOT_ID] = {
		id: VIRTUAL_ROOT_ID,
		children: getIds(rootLevelPages),
		parentId: null,
		hasChildren: true,
		isExpanded: true,
		isChildrenLoading: false,
		hasRestrictions: false,
		hasInheritedRestrictions: false,
		data: {
			title: '',
			status: '',
			blank: false,
			webui: '',
		},
	};

	dictionary[VIRTUAL_LOCAL_ROOT_ID] = {
		id: VIRTUAL_LOCAL_ROOT_ID,
		children: [],
		parentId: VIRTUAL_ROOT_ID,
		hasChildren: true,
		isExpanded: true,
		isChildrenLoading: false,
		hasRestrictions: false,
		hasInheritedRestrictions: false,
		data: {
			title: '',
			status: '',
			blank: false,
			webui: '',
		},
	};

	if (immutable) {
		dictionary = populateParents(dictionary);
	}

	return dictionary;
};

/**
 * Takes pagetree payload and transforms it to a structure consumable by
 * confluence/tree.
 */
export const flattenTreeData = (
	currentPage: any,
	rootLevelPages: any,
	isWhiteboardEmojiFGEnabled = false,
): TreeObjectItems<ContentTreeItem> => {
	let pages: TreeObjectItems<ContentTreeItem> = {};

	if (!currentPage) {
		return pages;
	}

	pages = flattenCurrentTree(currentPage, pages, isWhiteboardEmojiFGEnabled) || {};
	pages = flattenRootLevelTree(rootLevelPages, pages, false);

	const ancestors = getPages(currentPage.nearestAncestors);
	const children = getPages(currentPage.children);
	const currentLevel = [
		...[...getPages(currentPage.previousSiblings)].reverse(),
		{ id: currentPage.id, children },
		...getPages(currentPage.followingSiblings),
	];

	// Build references upwards to current page's ancestors
	ancestors.forEach((ancestor, i) => {
		const ancestorIsParent = i === 0;

		let childrenOfAncestor: any[] = [];
		if (ancestorIsParent) {
			childrenOfAncestor = currentLevel;
		} else {
			const childOfAncestor = ancestors[i - 1];
			childrenOfAncestor = [
				...[...getPages(childOfAncestor.previousSiblings)].reverse(),
				childOfAncestor,
				...getPages(childOfAncestor.followingSiblings),
			];
		}
		const page = pages[ancestor.id];
		if (page) {
			page.children = getIds(childrenOfAncestor);
		}
	});

	// Build references downwards to current page's children
	pages[currentPage.id].children = getIds(children);

	let deepestLevel = currentLevel;
	if (ancestors.length) {
		const deepestAncestor = ancestors[ancestors.length - 1];
		deepestLevel = [
			...[...getPages(deepestAncestor.previousSiblings)].reverse(),
			deepestAncestor,
			...getPages(deepestAncestor.followingSiblings),
		];
	}
	pages[VIRTUAL_LOCAL_ROOT_ID].children = getIds(deepestLevel);

	pages = populateParents(pages);

	return pages;
};

export const transformUIAttributes = (
	pages: any,
	currentPageId: any,
	expandedPages: any,
	loadingPages: any,
	highlightPages: any,
	cursors?: any,
): TreeObjectItems<ContentTreeItem> =>
	produce<TreeObjectItems<ContentTreeItem>>(pages, (transformedPages) => {
		for (const id in transformedPages) {
			if (transformedPages.hasOwnProperty(id)) {
				const transformedPage = transformedPages[id];
				transformedPage.isExpanded = expandedPages.has(id);
				transformedPage.isChildrenLoading = loadingPages.has(id);
				transformedPage.data.isSelected = transformedPage.id === currentPageId;
				transformedPage.data.isHighlighted = highlightPages?.includes(transformedPage.id) || false;
				if (
					cursors?.previous?.id === transformedPage.id ||
					cursors?.following?.id === transformedPage.id
				) {
					transformedPage.data.isCursor = true;
				}
			}
		}
	});

export const populateParents = (pages: any): TreeObjectItems<ContentTreeItem> =>
	produce<TreeObjectItems<ContentTreeItem>>(pages, (newPages) => {
		const pageIds = Object.keys(newPages);
		// assign parent id's based on parents children
		pageIds.forEach((id) => {
			const page = newPages[id];
			if (page && page.children && page.children.length) {
				page.children.forEach((child) => {
					if (
						typeof child === 'string' &&
						// In some cases one page can have both VIRTUAL_LOCAL_ROOT_ID and VIRTUAL_ROOT_ID as parent.
						// In this situation VIRTUAL_LOCAL_ROOT_ID has higher priority.
						!(page.id === VIRTUAL_LOCAL_ROOT_ID && newPages[child].parentId === VIRTUAL_ROOT_ID)
					) {
						newPages[child].parentId = page.id;
					}
				});
			}
		});
	});

export const setChildren = (
	pages: any,
	expandedParentId: any,
	childrenIds: any,
): TreeObjectItems<ContentTreeItem> => {
	const pagesWithChildren = produce(pages, (pagesWithChildren) => {
		pagesWithChildren[expandedParentId].children = childrenIds;
	});

	const withMovedChildren = moveChildrenIfNeeded(pagesWithChildren, childrenIds, expandedParentId);

	return populateParents(withMovedChildren);
};

const moveChildrenIfNeeded = (pages, childrenIds, latestParentId) =>
	produce(pages, (newPages) => {
		childrenIds.forEach((childId) => {
			if (
				newPages[childId] &&
				newPages[childId].parentId &&
				newPages[childId].parentId !== latestParentId
			) {
				const oldParent = newPages[newPages[childId].parentId];
				oldParent.children = without(oldParent.children, childId);
			}
		});
	});

const handleSyntheticItem = (
	syntheticItem,
	allPages: TreeObjectItems<ContentTreeItem>,
	rootId,
	syntheticItemIsDraft,
): TreeObjectItems<ContentTreeItem> => {
	allPages = { ...allPages };

	if (syntheticItem && syntheticItem.id) {
		if (
			!allPages[syntheticItem.id] &&
			!allPages[rootId].children.some((id) => id === syntheticItem.id)
		) {
			// If an item with same id isn't present in the tree we got from the server - put it at the very top.
			// Otherwise, we'll leave it where it was, and just override its properties
			allPages[rootId] = {
				...allPages[rootId],
				children: [syntheticItem.id, ...allPages[rootId].children],
			};
		}

		const existingPageData = allPages[syntheticItem.id]?.data || {};

		allPages[syntheticItem.id] = {
			...syntheticItem,
			data: syntheticItemIsDraft
				? {
						...existingPageData,
						...syntheticItem.data,
					}
				: {
						...syntheticItem.data,
						...existingPageData,
					},
		};
	}
	return allPages;
};

const correctTreeFlattener = (currentPage, rootLevelPages, isWhiteboardEmojiFGEnabled = false) => {
	return currentPage
		? flattenTreeData(currentPage, rootLevelPages, isWhiteboardEmojiFGEnabled)
		: flattenRootLevelTree(rootLevelPages);
};

const determineInitiallyExpandedPages = //only gets doesn't set.
	(pages, expandedPages, currentPage, rootLevel, pageTreeStateUpdatesContainer) => {
		/**
		 * Determines the initially expanded subtree after pages are fetched.
		 * The current page's ancestors are the only pages that will have children,
		 * which is how initially expanded subtree is determined below. Simply sets State
		 *
		 * @param {object} pages
		 */

		for (const id in pages) {
			const page = pages[id];
			if (page.hasChildren && childrenAreLatest(page) && page.isExpanded) {
				expandedPages.add(page.id);
			}
		}

		// Don't expand during creation flow for content that has inline
		// renaming instead of a create content route, such as folders
		const editingTitle = pageTreeStateUpdatesContainer?.state?.editingTitle;
		const isCurrentPageInCreationFlow =
			editingTitle?.isNewContent && currentPage?.id === editingTitle?.contentId;

		if (currentPage && !rootLevel && !isCurrentPageInCreationFlow) {
			expandedPages.add(currentPage.id);
			const ancestors = currentPage.nearestAncestors.nodes;
			ancestors.forEach((page) => {
				expandedPages.add(page.id);
			});
		}

		return expandedPages;
	};

export const deriveStateFromData = (
	data: any,
	spaceKey: any,
	state: any,
	isPeekingFromBlogs: any,
	syntheticItem: any,
	syntheticItemIsDraft: any,
	pageTreeStateUpdatesContainer?: any,
	isWhiteboardEmojiFGEnabled = false,
) => {
	//need to determine if there is no homepage, if not we must populate rootLevelPages / syntheticLocalRoot
	const { space } = data;
	const rootLevelPages = { nodes: [], pageInfo: {} };

	let currentPage;
	let rootLevel = null;
	if (data?.ptpage?.followingSiblings && !data?.currentPage) {
		currentPage = {
			...data.ptpage,
			previousSiblings: { nodes: [], pageInfo: {} },
			children: { nodes: [], pageInfo: {} },
			nearestAncestors: { nodes: [], pageInfo: {} },
		};
		rootLevel = VIRTUAL_LOCAL_ROOT_ID; // if we are displaying only root level pages, always use virtual root
	} else {
		currentPage = data?.currentPage;
	}

	const serverPages = correctTreeFlattener(currentPage, rootLevelPages, isWhiteboardEmojiFGEnabled);
	// @ts-ignore
	let allPages = mergePages(state.pages, serverPages);
	const spaceHomePageId = space?.homepage?.id;

	const rootId = rootLevel || getRootId(allPages, currentPage?.id, spaceHomePageId);

	const cursors = getDirectionalCursors(currentPage, rootId, {});

	//set the space overview root now that we have it
	//"noHomepageFound" prevents infinite loop if space.homepage.id is undefined & returns
	//the orphaned page root
	const blogPeekRoot = isPeekingFromBlogs
		? (space.homepage && space.homepage.id) || 'noHomepageFound'
		: undefined;

	allPages = handleSyntheticItem(syntheticItem, allPages, rootId, syntheticItemIsDraft);

	const expandedPages = determineInitiallyExpandedPages(
		allPages,
		state.expandedPages,
		currentPage,
		rootLevel,
		pageTreeStateUpdatesContainer,
	);
	const newState = {
		rootId,
		pages: allPages,
		space,
		spaceKey,
		expandedPages,
		isTreeTruncatedPrevious: isPageTreeTruncatedPrevious(currentPage),
		isTreeTruncatedFollowing: isPageTreeTruncatedFollowing(currentPage),
		cursors,
		initialPages: currentPage,
		blogPeekRoot,
	};
	return newState;
};
